// @ts-nocheck

import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import theme from '../../styled-components/dark';
import type { BasketMode } from '../../types/basket';

const TAB_HEIGHT = 1.5;
const TAB_BORDER_RADIUS = 1;
const ANIMATION_DURATION = 200;

const Tabs = styled.div`
  position: relative;
  height: ${TAB_HEIGHT}em;
  width: 100%;
  border-radius: ${TAB_BORDER_RADIUS}em;
  background-color: ${theme.BasketTabs.default.backgroundColor};

  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Tab = styled.div`
  position: relative;
  width: 100%;
  text-align: center;
  margin: 0em 0.4em;

  font-weight: bold;
  font-size: 0.95em;

  z-index: 2;
  transition: color ${ANIMATION_DURATION}ms ease-in-out;

  ${({ $selected, $disabled }) => {
    if ($selected) {
      return css`
        color: ${theme.BasketTabs.selected.color};
        pointer-events: none;

        &:after {
          background-color: ${theme.BasketTabs.selected.circleBackgroundColor};
        }
      `;
    }
    if ($disabled) {
      return css`
        opacity: 0.4;
        color: ${theme.BasketTabs.disabled.color};
        cursor: default;
      `;
    }
    return css`
      color: ${theme.BasketTabs.default.color};
      cursor: pointer;
    `;
  }}
`;

const Selected = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
  transition: transform ${ANIMATION_DURATION}ms ease-in-out;

  ${({ $width, $position }) => {
    if ($width) {
      return css`
        width: ${$width}%;
        height: ${TAB_HEIGHT}em;
        background-color: ${theme.BasketTabs.selected.backgroundColor};
        border-radius: ${TAB_BORDER_RADIUS}em;
        transform: translateX(${$position * 100}%);
      `;
    }
  }};
`;

type BasketTabType = { id: BasketMode; label: string; disabled: boolean };

type BasketTabsPropsType = {
  tabs: Array<BasketTabType>;
  selected?: BasketMode;
  onSelectionChange?: (selected: BasketMode) => void;
  className?: string;
};

const BasketTabs = ({
  tabs = [],
  onSelectionChange = () => {},
  selected = tabs?.[0],
  className,
}: BasketTabsPropsType) => {
  const tabsLength = useMemo(() => tabs.length, [tabs]);
  const selectedPosition = useMemo(() => tabs.findIndex((tab) => tab.id === selected), [tabs, selected]);

  const onTabClick = useCallback(
    (tabId: BasketMode) => () => {
      if (selected !== tabId) {
        onSelectionChange(tabId);
      }
    },
    [onSelectionChange, selected],
  );

  return (
    <Tabs className={className}>
      {tabs.map((tab) => (
        <Tab
          key={`tab-${tab.id}`}
          $selected={tab.id === selected}
          onClick={onTabClick(tab.id)}
          $disabled={tab.disabled}
        >
          {tab.label}
        </Tab>
      ))}
      <Selected $position={selectedPosition} $width={100 / tabsLength} />
    </Tabs>
  );
};

export default BasketTabs;
